import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, ArrowLeft, Droplets } from 'lucide-react';
import CustomerNavigation from '@/components/CustomerNavigation';
import SEO from '@/components/SEO';

import { SampleTypeSelector } from './SampleTypeSelector';
import { LocationFields } from './LocationFields';
import { ContactFields } from './ContactFields';
import { InstructionsCard } from './InstructionsCard';
import { API, getInstructions } from './helpers';

export default function OrderSample() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  
  const [formData, setFormData] = useState({
    sample_type: 'algae',
    location_name: '',
    location_address: '',
    latitude: '',
    longitude: '',
    preferred_date: '',
    depth: '3m',
    priority: 'normal',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    special_instructions: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'sample_type') setShowInstructions(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.location_name || !formData.preferred_date) {
      toast.error('Vennligst fyll ut alle obligatoriske felt');
      return;
    }
    try {
      setLoading(true);
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };
      await axios.post(`${API}/sample-orders`, submitData, { withCredentials: true });
      toast.success('Bestilling sendt! Du far beskjed nar den blir behandlet.');
      navigate('/customer/orders');
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Feil ved oppretting av bestilling');
    } finally {
      setLoading(false);
    }
  };

  const instructions = getInstructions(formData.sample_type, formData.depth);
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-white flex">
      <SEO title="Bestill prove" description="Bestill provetaking fra Microsafe" />
      <CustomerNavigation />
      <main className="flex-1 min-h-screen">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 sm:py-8 animate-fade-in">
          
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => navigate('/customer/orders')} 
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
              data-testid="back-to-orders"
            >
              <ArrowLeft className="w-4 h-4" />
              Tilbake til bestillinger
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/25">
                <Droplets className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-heading font-bold text-slate-900 tracking-tight" data-testid="order-sample-title">
                  Bestill provetaking
                </h1>
                <p className="text-sm text-slate-500">Fyll ut skjemaet under for a bestille provetaking</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  
                  <SampleTypeSelector 
                    sampleType={formData.sample_type} 
                    onChange={handleChange} 
                  />

                  <LocationFields 
                    formData={formData} 
                    onChange={handleChange} 
                    today={today} 
                  />

                  <ContactFields 
                    formData={formData} 
                    onChange={handleChange} 
                  />

                  {/* Special Instructions */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Spesielle onsker/instruksjoner
                    </label>
                    <textarea
                      name="special_instructions"
                      value={formData.special_instructions}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Eventuelle spesielle onsker eller informasjon vi bor vite..."
                      className="w-full px-4 py-3 bg-slate-50 border-transparent focus:bg-white focus:border-sky-500 focus:ring-2 focus:ring-sky-100 rounded-xl text-sm transition-all resize-none"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => navigate('/customer/orders')}
                      className="flex-1 px-6 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium"
                      data-testid="order-cancel-button"
                    >
                      Avbryt
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] transition-all text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2"
                      data-testid="order-submit-button"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sender...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send bestilling
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Instructions Sidebar */}
            <div className="lg:col-span-1">
              <InstructionsCard 
                instructions={instructions}
                showInstructions={showInstructions}
                setShowInstructions={setShowInstructions}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
